import Box from "@mui/material/Box";
import type {
    AggregationResourceListItem,
    ResourceDisplayReference,
} from "crqlar-react-common/dist/models/aggregation/aggregation.model";
import type {DailyStayStatus} from "crqlar-react-common/dist/models/dailyList/dailyList.model";
import {getStayStatusDisplay} from "crqlar-react-common/dist/components/uiElements/StayStatusElement";
import {useTranslation} from "react-i18next";
import {
    AccommodationElement,
    FacilityElement,
    ResourceDisplayReferenceElement,
    RestaurantElement,
    RestaurantTableElement,
    StaffElement,
} from "crqlar-react-common/dist/components/uiElements/ResourceElement";

export {
    AccommodationElement,
    FacilityElement,
    ResourceDisplayReferenceElement,
    RestaurantElement,
    RestaurantTableElement,
    StaffElement,
};

interface AggregationResourceElementProps {
    resource: AggregationResourceListItem;
    asPrint?: boolean;
}

export function AggregationResourceElement({resource, asPrint}: AggregationResourceElementProps) {
    return <ResourceDisplayReferenceElement resource={resource} asPrint={asPrint}/>;
}

export function AccommodationReferences({resources}: {resources: ResourceDisplayReference[] | undefined}) {
    if (!resources?.length) return null;

    return (
        <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
            {resources.map(resource => (
                <AccommodationElement
                    key={resource.id}
                    name={resource.name ?? ""}
                    color={resource.tagColor}
                />
            ))}
        </Box>
    );
}

interface RoomStatusElementProps {
    resources: ResourceDisplayReference[] | undefined;
    stayStatus: DailyStayStatus | undefined;
    onClick?: () => void;
    asPrint?: boolean;
}

export function RoomStatusElement({resources, stayStatus, onClick, asPrint = false}: RoomStatusElementProps) {
    const {t} = useTranslation();
    if (!resources?.length) return null;

    const statusDisplay = getStayStatusDisplay(stayStatus);
    const statusLabel = statusDisplay ? t(statusDisplay.label) : undefined;

    if (asPrint) {
        const roomNames = resources.map(resource => resource.name).filter(Boolean).join(", ");
        return <>{statusLabel ? `${roomNames} (${statusLabel})` : roomNames}</>;
    }

    return (
        <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
            {resources.map(resource => (
                <AccommodationElement
                    key={resource.id}
                    name={resource.name ?? ""}
                    color={statusDisplay?.color ?? resource.tagColor}
                    onClick={onClick}
                    tooltipContent={statusLabel
                        ? `${t("Room")} ${resource.name ?? ""} — ${statusLabel}`
                        : undefined}
                />
            ))}
        </Box>
    );
}
